import { useState, useEffect } from 'react';
import { RemoteConfigKey } from '../../../../../../firebase/remoteConfig';
import { useStoreState } from '../../../../../../models/hooks';

const SHOW_BUTTON_BEFORE_MS = 5 * 60 * 1000; // 5 minutes
// const GRACE_PERIOD_MS = 5 * 60 * 1000; // 5 minutes
const TICK_INTERVAL_MS = 10_000; // 10 seconds

interface InterviewVisibility {
  showButton: boolean;
  showBanner: boolean;
}

const calculateVisibility = (fromDate: Date | null, toDate: Date | null, GRACE_PERIOD_MS: number): InterviewVisibility => {
  if (!fromDate) return { showButton: false, showBanner: false };

  const now = Date.now();
  const endMs = (toDate ? toDate.getTime() : fromDate.getTime()) + GRACE_PERIOD_MS;
  const buttonWindowStartMs = fromDate.getTime() - SHOW_BUTTON_BEFORE_MS;

  // Rule 1: Once we pass the end time + grace period, hide everything
  if (now >= endMs) return { showButton: false, showBanner: false };

  // Rule 2: Otherwise show the banner, and show button if we are in the time window
  return { showBanner: true, showButton: now >= buttonWindowStartMs };
};

export function useInterviewVisibility(fromDate: Date | null, toDate: Date | null): InterviewVisibility {
  const gracePeriod = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.JOIN_VIDEO_GRACE_PERIOD_IN_MINUTE];
  });
  const GRACE_PERIOD_MS = (gracePeriod ?? /* istanbul ignore next */ 0) * 60 * 1000;
  // Lazy initialization: only run the time calculation once on initial load
  const [visibility, setVisibility] = useState(() => calculateVisibility(fromDate, toDate, GRACE_PERIOD_MS));

  useEffect(
    () => {
      // Helper: Updates state ONLY if the boolean values actually changed.
      // Returning `prev` triggers a state bailout, preventing unnecessary re-renders.
      const syncVisibility = () => {
        setVisibility((prev) => {
          const next = calculateVisibility(fromDate, toDate, GRACE_PERIOD_MS);
          if (prev.showButton === next.showButton && prev.showBanner === next.showBanner) {
            return prev;
          }
          return next;
        });
      };

      // Step 1: Immediately sync UI just in case the dates changed
      syncVisibility();

      // Step 2: If there's no interview scheduled, we don't need timers
      if (!fromDate) return;

      const now = Date.now();
      const endMs = (toDate ? toDate.getTime() : fromDate.getTime()) + GRACE_PERIOD_MS;
      const buttonWindowStartMs = fromDate.getTime() - SHOW_BUTTON_BEFORE_MS;

      // Step 3: If the interview is completely over, we don't need timers
      if (now >= endMs) return;

      let timeoutId: NodeJS.Timeout;
      let intervalId: NodeJS.Timer;

      // Helper: Starts a background interval to check if the interview has ended
      const startPolling = () => {
        intervalId = setInterval(syncVisibility, TICK_INTERVAL_MS); // Checks every 10 seconds
      };

      if (now < buttonWindowStartMs) {
        // SCENARIO A: The interview is still far away.
        // Calculate the exact milliseconds until the "Join" button needs to appear.
        const waitTime = buttonWindowStartMs - now;

        // Set a precise clock for that exact moment.
        timeoutId = setTimeout(() => {
          syncVisibility(); // Wake up and show the button!
          startPolling(); // Now start checking for the end of the interview.
        }, waitTime);
      } else {
        // SCENARIO B: We are already inside the active interview window.
        // The button is already visible, so jump straight to checking for the end.
        startPolling();
      }

      // eslint-disable-next-line consistent-return
      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fromDate, toDate],
  );

  return visibility;
}
